const THEME_STORAGE_KEY = 'h2o_theme';
export const SYSTEM_THEME = 'system';
const LIGHT_THEME = 'light';
const DARK_THEME = 'dark';
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: light)';

export function normalizeTheme(value) {
  return value === LIGHT_THEME ? LIGHT_THEME : DARK_THEME;
}

export function applyTheme(theme) {
  const normalizedTheme = normalizeTheme(theme);

  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = normalizedTheme;
    document.documentElement.style.colorScheme = normalizedTheme;
  }

  return normalizedTheme;
}

export function getStoredThemeOverride() {
  if (typeof window === 'undefined') return '';

  try {
    const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return storedTheme === LIGHT_THEME || storedTheme === DARK_THEME ? storedTheme : '';
  } catch {
    return '';
  }
}

export function getSystemTheme() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return DARK_THEME;
  }

  return window.matchMedia(SYSTEM_THEME_QUERY).matches ? LIGHT_THEME : DARK_THEME;
}

export function resolveTheme() {
  return getStoredThemeOverride() || getSystemTheme();
}

export function getThemePreference() {
  return getStoredThemeOverride() || SYSTEM_THEME;
}

export function initializeTheme() {
  return applyTheme(resolveTheme());
}

export function setThemeOverride(theme) {
  const normalizedTheme = applyTheme(theme);

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
    } catch {
      // Ignore storage failures and still apply the theme for the current session.
    }
  }

  return normalizedTheme;
}

export function hasThemeOverride() {
  return Boolean(getStoredThemeOverride());
}

export function clearThemeOverride() {
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    } catch {
      // Ignore storage failures and still apply the theme for the current session.
    }
  }

  return applyTheme(getSystemTheme());
}

export function setThemePreference(theme) {
  if (theme === SYSTEM_THEME) {
    return clearThemeOverride();
  }

  return setThemeOverride(theme);
}

export function watchSystemTheme(callback) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return () => {};
  }

  const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
  const handleChange = (event) => {
    callback(event.matches ? LIGHT_THEME : DARK_THEME);
  };

  if (typeof mediaQuery.addEventListener === 'function') {
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }

  mediaQuery.addListener(handleChange);
  return () => mediaQuery.removeListener(handleChange);
}
